import type { ModalProps } from '@mantine/core'
import { Modal } from '@mantine/core'
import { QrCodeScanner } from 'components/QrCodeScanner/QrCodeScanner'
import { cloneDeep } from 'lodash'
import React from 'react'
import { SharedSettings } from 'settings/SharedSettings'
import { init } from 'stores/_utils/simple-store'
import type { ModalSettings } from 'utils/mantineModalSettings'

const modalStore = init<Map<string, ModalSettings>>(new Map())

const openModal = (props: Omit<ModalSettings, 'opened'>) => {
  modalStore.setState((prev) => {
    const updatedModal = prev ? cloneDeep(prev) : new Map()
    const existingModal = updatedModal.get(props.modalId)

    if (existingModal) {
      existingModal.onClose?.()

      updatedModal.delete(props.modalId)

      // eslint-disable-next-line no-console
      console.warn(`Modal with id ${props.modalId} already exists. It will be replaced.`)
    }

    updatedModal.set(props.modalId, { ...props })

    return updatedModal
  })
}

const closeModal = (modalId: string) => {
  modalStore.setState((prev) => {
    const updatedModal = prev ? cloneDeep(prev) : new Map()

    const setting = Array.from(updatedModal).find(
      ([, value]: [string, ModalSettings]) => value.modalId === modalId,
    )

    if (setting) {
      const [id, existingModal] = setting

      existingModal.onClose?.()

      updatedModal.delete(id)
    }

    return updatedModal
  })
}

const Overlay: React.FC = () => {
  const modalSettings = modalStore.useSelector((settings) => settings)
  const isScanning = QrCodeScanner.useIsScanning()

  let zIndex = SharedSettings.DEFAULT_ABOVE_ALL_MODAL_OVERLAY_Z_INDEX

  const modals = Array.from(modalSettings).map(([key, value]: [string, ModalSettings]) => {
    const { styles, modalId, onClose, ...props } = value

    const overrideProp: Omit<ModalProps, 'onClose' | 'opened'> = {
      ...props,
      styles: {
        ...styles,
        close: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...((styles as any)?.close || {}),
          order: -1, // make sure the close button is on the left
          marginRight: 10, //erase the default margin
          marginLeft: 0, //erase the default margin
          scale: '1.5', // make the close button bigger
        },
        header: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...((styles as any)?.header || {}),
          justifyContent: 'start', // make sure the close button is on the left
        },
        title: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...((styles as any)?.title || {}),
          // make the title bigger
          fontWeight: 'bold',
        },
        root: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...((styles as any)?.root || {}),
          position: 'fixed',
          display: isScanning ? 'none' : 'block', //Hide Map
          zIndex: zIndex++, // make sure this is great than any other native or custom modals,
        },
      },
    }

    return {
      id: modalId,
      group: key,
      onClose: onClose,
      overrideProp,
    }
  })

  return (
    <>
      {modals.map((modal, index) => (
        <Modal
          opened
          key={modal.id}
          onClose={() => {
            if (modal.id) {
              closeModal(modal.id)
            }
          }}
          {...modal.overrideProp}
          // Mantine listens for Escape on window, so only the topmost modal may close on it
          closeOnEscape={index === modals.length - 1 && modal.overrideProp.closeOnEscape !== false}
        />
      ))}
    </>
  )
}

export const AboveAllModal = {
  close: closeModal,
  open: openModal,
  Overlay: Overlay,
}
